// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## rhythmMath.js
//
// "How many quarter notes are in a dotted half?", etc...
//
import InteractivePrototype from './interactivePrototype.ts';
import Rhythm from '../uTheory.Notation/rhythm.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import * as Font from '../uTheory.Font/font.ts';
import utils from '../utils.ts';
import RhythmPanel from './rhythmPanel.ts';

class RhythmMath extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.settings = {
      instructions: {
        ariaLive: 'assertive',
        ariaLiveRegion: 'all',
      },
      editor: false,
      notation: false,
      piano: false,
      letters: options.understandingDots
        ? false
        : { numPad: { check: true, trash: true, hotKeys: true } },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.layout = {
      instructions: { percent: 30 },
      letters: {
        percent: 70,
        visible: !options.understandingDots,
      },
      rhythmPanel: {
        percent: 70,
        visible: !!options.understandingDots,
      },
    };
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    // normal options:
    // testMode -- when true, questions are generated by the checkpoint
    //      app calling newCheckpointQuestion.

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  createCustomDivs() {
    if (!this.options.understandingDots) return;
    const rhythmPanelDiv = this.createDiv(
      'rhythmPanelDiv',
      {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
      },
      true
    );

    this.displayDiv.insertBefore(rhythmPanelDiv, this.lettersDiv);
    this.rhythmPanel = new RhythmPanel(rhythmPanelDiv, {
      dot: false,
      check: true,
      rest: false,
      onChange: (duration) => {
        this.currentAnswer = '';
        const rest = this.currentQuestion.rests ? 'r' : '';
        this.addNumber(duration + rest);
      },
      onAction: (duration) => {
        this.check(duration);
      },
    });

    this.layout.rhythmPanel.div = this.rhythmPanelDiv;
    this.layout.rhythmPanel.callback = this.rhythmPanel.resize.bind(this.rhythmPanel);
  }

  generateQuestions(questions = this.options.questions) {
    const basicQuestionPrompt =
      'How many {question} are in one {question}? <span id="rhythm-math-answer-span" aria-live="polite"></span>';
    const understandingDotsQuestionPrompt = `Complete the equation:<br />
      {question} = {question} +  <span id="rhythm-math-answer-span" aria-live="polite"></span>`;
    this.questionPrompt = this.options.understandingDots
      ? understandingDotsQuestionPrompt
      : basicQuestionPrompt;
    this.questions = [];

    if (this.options.understandingDots && !this.options.questions) {
      throw new utils.RERR(
        'Config',
        'rhythmMath can only be used in understandingDots mode with manual questions.'
      );
    }

    // If questions are specified in options, we use those:
    if (questions) {
      this.questions = this.options.understandingDots
        ? questions.map(RhythmMath.createUnderstandingDotsQuestion.bind(this))
        : questions.map(RhythmMath.createQuestion.bind(this));
      return;
    }

    const maxQ = this.options.maxQuestions || 10;
    // Otherwise, we will generate them:
    if (typeof this.options.skills !== 'undefined') {
      this.makeQuestions(this.options.skills);
    } else {
      this.makeQuestions(['basic_rhythm_math', 'dotted_rhythm_math'], maxQ);
    }
  }

  /**
   * @param {string} q as 'h.', etc...
   */
  static createUnderstandingDotsQuestion(q) {
    const rests = q.includes('r');
    const withRest = (noteValue) => Font.toHTML('rhythm', noteValue + (rests ? 'r' : ''));

    const values = ['w', 'h', 'q', '8', '16', '32'];
    const baseValue = new Rhythm(`${q.toString().replace('r', '')}`).baseDuration;

    const dottedBaseValueWithRest = `${baseValue}.${rests ? 'r' : ''}`;
    const dotIndex = values.findIndex((value) => value === baseValue) + 1;
    if (!dotIndex) {
      throw new utils.RERR(
        'Bad Question',
        `The string ${q} is invalid as an understanding dots question.`
      );
    }
    const dotValue = values[dotIndex];
    const answer = dotValue;
    const skills = ['dotted_rhythm_math'];
    const text = [Font.toHTML('rhythm', dottedBaseValueWithRest), withRest(baseValue)];
    return {
      rests,
      baseValue,
      dottedBaseValueWithRest,
      dotValue,
      text,
      answer,
      skills,
      question: q,
    };
  }

  static createQuestion(q) {
    const parts = q.split('/');
    const text = [Font.toHTML('rhythm', parts[0]), Font.toHTML('rhythm', parts[1])];
    const r1 = new Rhythm(parts[0].replace('r', ''));
    const r2 = new Rhythm(parts[1].replace('r', ''));
    const answer = r2.getFraction().divide(r1).value();
    const skill = r1.dots || r2.dots ? 'dotted_rhythm_math' : 'basic_rhythm_math';
    return {
      text,
      answer,
      skills: [skill],
      question: q,
    };
  }

  newQuestion() {
    this.currentAnswer = '';
    super.newQuestion();
    this.displayAnswer('');
    if (this.options.understandingDots) {
      this.rhythmPanel.reset();
      this.rhythmPanel.showRests(this.currentQuestion.rests);
    }
  }

  makeQuestions(skills) {
    // skills should be basic_rhythm_math or dotted_rhythm_math
    const g = new QuestionGenerator();
    const rawQuestions = g.generate(skills, this.options.maxQuestions || 10);
    this.questions = rawQuestions.map((q) => RhythmMath.createQuestion(q), this);
  }

  addListeners() {
    if (this.options.understandingDots) return;
    const dispatcher = function (arg) {
      switch (arg) {
        case 'check':
          this.check(arg);
          break;
        case 'trash':
          this.trash();
          break;
        default:
          this.addNumber(arg);
          break;
      }
    };

    this.letterPanel.addCustomButtonCallback(dispatcher.bind(this), this);
  }

  displayAnswer(answer = '') {
    if (this.options.understandingDots) {
      answer = Font.toHTML('rhythm', answer);
    }
    this.displayDiv.querySelector('#rhythm-math-answer-span').innerHTML = answer;
  }

  addNumber(arg) {
    if (typeof this.currentAnswer === 'undefined') {
      this.currentAnswer = '';
    }
    this.currentAnswer += arg;
    this.displayAnswer(this.currentAnswer);
  }

  trash() {
    this.currentAnswer = this.currentAnswer.slice(0, this.currentAnswer.length - 1);
    this.displayAnswer(this.currentAnswer);
  }

  check() {
    if (!this.options.testMode && this.rightAnswerDelay) return;
    if (this.optionsTrue('testMode')) {
      this.checkpointAnswer(this.currentAnswer);
      return;
    }

    if (this.currentAnswer === '') {
      this.checkMark.warn('You must answer first.');
      return;
    }

    const correct = this.options.understandingDots
      ? this.currentQuestion.dotValue === this.rhythmPanel.answer.duration
      : this.currentQuestion.answer === Number(this.currentAnswer);

    if (correct) {
      this.rightAnswer(500);
    } else {
      this.wrongAnswer();
      this.currentAnswer = '';
      if (this.options.understandingDots) this.rhythmPanel.clearAnswer();
      this.trash();
    }
  }

  gameOver() {
    if (this.options.understandingDots) this.rhythmPanel.exit();
    this.updateInstructions('Good work!', '');
    super.gameOver();
  }

  playAnswer() {}

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.currentAnswer = '';
      this.generateQuestions([questionString]);
      this.currentQuestion = this.questions[0];
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(answer) {
    answer = answer || '';
    // eslint-disable-next-line eqeqeq
    const correct = answer == this.currentQuestion.answer;

    const answerAndData = {
      question: this.currentQuestion.question,
      questionType: 'RhythmMath',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: `${answer}`,
      studentAnswerSVG: null,
      correctAnswerText: `${this.currentQuestion.answer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
    return answerAndData;
  }
}

RhythmMath.registry = [
  {
    description: 'Rhythm Math',
    skills: ['basic_rhythm_math', 'dotted_rhythm_math'],
  },
];

export default RhythmMath;
